/**
 * 쇼룸 도메인 타입.
 *
 * 카탈로그 데이터(data/)와 그것을 읽는 렌더·UI 레이어가 같은 형태를 공유해야 하는데,
 * 그 형태를 각 파일에 흩어 두면 카탈로그에 항목을 추가할 때 어디까지 맞춰야 하는지
 * 알 수 없습니다. 그래서 "쇼룸의 차 한 대"와 옵션들의 형태를 여기 한곳에 둡니다.
 *
 * 런타임 값(기본값·목록)은 여기 두지 않습니다. 그건 data/의 몫입니다.
 */
import type { MeshSelector, MeshSelectorMap } from './three/gltf/meshRoles';
import type { DecalImageId } from './three/materials/decalImages';

/** [x, y, z]. 씬 좌표와 실측 치수 전반에 씁니다. */
export type Vector3Tuple = [number, number, number];

/** [가로, 세로] (m). 판과 데칼의 실측 크기. */
export type Size2Tuple = [number, number];

/**
 * 운전석이 어느 쪽에 있는지.
 *
 * 전면이 +X, 위가 +Y인 앱 좌표계에서 차량의 오른쪽은 +Z입니다.
 * 따라서 'left'(좌핸들)는 실내 눈이 -Z, 'right'(우핸들)는 +Z입니다.
 *
 * 왜 모델별 속성인가:
 *   한때 상수 하나로 처리했습니다. 그때까지 들어온 에셋이 모두 운전석이 -Z 쪽
 *   이었기 때문입니다. 그 뒤 우핸들 에셋(클래식 미니, 지금은 카탈로그에서 제거됨)이
 *   들어오면서 가정이 깨졌습니다. 다시 상수로 접지 않는 이유는, 부호를 잘못 넣으면
 *   카메라가 조수석에 앉는데 그건 렌더를 봐야만 알게 되는 종류의 버그라서입니다.
 *   매니페스트에 적어 두면 테스트가 눈 좌표와 대조해 줍니다.
 *
 *   현재 카탈로그의 실내 에셋은 전부 'left'입니다. 즉 'right'는 지금 쓰이지 않지만,
 *   우핸들 차를 다시 넣을 때 이 규약이 없으면 같은 실수를 반복하게 됩니다.
 */
export type DriverSide = 'left' | 'right';

/* ---------- 차량 ---------- */

/**
 * 파워트레인 종류. 계기판에 무엇을 띄울지와 변속 표시를 정합니다.
 *
 * category에 '전기 세단'처럼 이미 적혀 있는데 왜 또 두는가:
 *   category는 사람이 읽는 부제입니다. 그 문구를 파싱해서 물리·계기 분기를 태우면
 *   문구를 다듬는 순간 조용히 어긋납니다(brand를 name에서 잘라 쓰지 않는 것과 같은
 *   이유). 표시 문구와 기계적 의미는 따로 둡니다.
 *
 * phev를 ev와 나누는 이유:
 *   Sorento PHEV는 엔진과 6단 자동변속기를 그대로 갖고 있어서 회전수와 단수가
 *   실제로 존재합니다. 계기판 관점에서는 내연기관과 같게 다뤄야 맞습니다.
 */
export type Powertrain = 'ice' | 'phev' | 'ev';

/**
 * 생산 연도.
 *
 * `end`가 없으면 "아직 생산 중"입니다. 0이나 현재 연도로 채우지 않는 이유는,
 * 둘 다 "언제 끝났다"라는 거짓을 말하기 때문입니다. 표기에서도 뒤쪽을 비워
 * `2019-`처럼 열린 구간으로 보여 줍니다(formatProductionYears).
 *
 * 어느 구간을 적는가:
 *   에셋이 속한 세대의 생산 기간입니다. 차명이 세대까지 좁혀지지 않는 경우가
 *   있어서 그렇습니다 — Sorento는 2002년부터 4세대에 걸쳐 나왔지만 우리가 가진
 *   에셋은 4세대(MQ4)뿐입니다. 명판 전체를 적으면 화면의 차와 무관한 숫자가 됩니다.
 */
export interface ProductionYears {
  start: number;
  end?: number;
}

/** GLB 에셋과 씬에 세울 때의 변환. */
export interface CarAsset {
  url: string;
  dracoDecoderPath: string;
  /** 원본 전면 방향(모델 로컬 좌표). rotationY 검증에 씁니다. */
  sourceFrontAxis: Vector3Tuple;
  rotationY: number;
  scale: number;
  position: Vector3Tuple;
}

/** 회전·스케일·이동을 모두 적용한 뒤의 월드 좌표 실측 치수. */
export interface CarBounds {
  rearX: number;
  frontX: number;
  groundY: number;
  roofY: number;
  halfWidth: number;
  frontWheelX: number;
  rearWheelX: number;
  wheelHalfTrack: number;
  tireOuterRadius: number;
}

/** 에셋이 화면을 독립 머티리얼로 갖고 있을 때의 화면 설정. */
export interface DisplayConfig {
  aspect: number;
  backgroundColor: string;
}

/**
 * 에셋에 화면이 없어서 우리가 얹는 판.
 * `normal`이 없으면 운전석 눈을 바라보게 돌립니다.
 */
export interface DisplayPanelConfig {
  position: Vector3Tuple;
  size: Size2Tuple;
  normal?: Vector3Tuple;
  backgroundColor: string;
}

/**
 * 에셋의 번호판 면에 쇼룸 로고를 입힐 때의 설정.
 *
 * aspect를 손으로 적는 이유:
 *   번호판 면의 실측 비율입니다. 메시 바운딩 박스에서 런타임에 뽑는 편이 어긋날
 *   일이 없어 좋지만, Model S는 앞·뒤 번호판이 프리미티브 하나에 들어 있어서
 *   (정점 8개 = 사각형 둘) 바운딩 박스가 차 길이만큼 벌어집니다. 그러면 "가장 얇은
 *   축"으로 판의 면을 찾는 규칙이 엉뚱한 축을 고릅니다. 그래서 bounds와 같은 방식,
 *   즉 조사 도구로 재서 근거를 주석에 남기는 쪽을 택했습니다.
 *
 *   재는 방법은 scripts/inspect-uv.mjs 가 찍어 주는 "화면 면적"입니다.
 *   판이 곡면이거나 테두리가 있으면 법선으로 가시면만 걸러 재야 정확합니다.
 */
export interface LicensePlateConfig {
  /** 번호판 가시면의 가로/세로 비. 이 비율로 캔버스를 만들어야 로고가 찌그러지지 않습니다. */
  aspect: number;
  /** 판 바닥색. 생략하면 licensePlate.ts의 기본값(밝은 회백색)을 씁니다. */
  backgroundColor?: string;
}

/**
 * 차량 표면에 판을 하나 얹을 때의 배치. 실측 좌표계(앱 좌표)로 적습니다.
 *
 * 데칼과 번호판 판이 같은 형태를 쓰는 이유는 하는 일이 같기 때문입니다 —
 * "실측한 평면에 이미지를 붙인 작은 판을 세운다". 그리는 컴포넌트(SurfaceDecal)도
 * 하나만 두고, 다른 것은 어디서 텍스처를 얻는지뿐입니다.
 */
export interface SurfacePlacement {
  position: Vector3Tuple;
  normal: Vector3Tuple;
  /** 이미지 위쪽이 향할 방향. 법선이 거의 수직인 면에서만 필요합니다. */
  up?: Vector3Tuple;
  size: Size2Tuple;
  metalness?: number;
  roughness?: number;
  envMapIntensity?: number;
}

/** 표면에 덧붙이는 이미지 데칼. */
export interface SurfaceDecalConfig extends SurfacePlacement {
  id: string;
  image: DecalImageId;
}

/**
 * 에셋에 번호판 "자리"는 있지만 그 면을 따로 고를 수 없을 때 얹는 판.
 *
 * 왜 LicensePlateConfig(역할로 텍스처 입히기)로 안 되는가 — Taycan이 그 경우입니다:
 *   Taycan 노즈에는 번호판 자리가 형상으로 분명히 있습니다. 앞범퍼 아래쪽에
 *   513.9 x 107.3 mm 평면(법선 일관성 0.998)이 가운데 붙어 있고, 크기가 유럽 번호판
 *   (520 x 110 mm)과 거의 같습니다. 그런데 그 면이 속한 머티리얼(`Carro_Plastico`)과
 *   메시(`Object_70`)는 둘 다 차 전체에 걸쳐 있습니다(각각 9,432 / 2,606 삼각형,
 *   x -2.0~2.5). 그래서 역할로 텍스처를 주면 번호판이 아니라 차 곳곳의 플라스틱
 *   트림이 함께 물듭니다.
 *
 *   그래서 그 자리에 판을 하나 덧붙입니다. 실내 화면에서 display(에셋에 화면이 있음)와
 *   displayPanel(없어서 우리가 얹음)을 나눈 것과 같은 구조입니다.
 *
 * 비율(aspect)이 없는 이유: size에서 그대로 뽑습니다. 따로 적으면 판 크기를 고칠 때
 * 둘이 어긋나서 로고가 찌그러집니다(displayPanel도 같은 이유로 적지 않습니다).
 */
export interface PlatePanelConfig extends SurfacePlacement {
  /** 판 바닥색. 생략하면 licensePlate.ts의 기본값을 씁니다. */
  backgroundColor?: string;
}

/**
 * 원본 에셋의 출처 표기.
 *
 * 개인용으로만 쓰는 저장소라서 필수는 아닙니다. 다만 나중에 공개할 일이 생기면
 * CC-BY 계열은 표기가 의무이므로, 아는 출처는 적어 두는 편이 낫습니다.
 * 원본 라이선스 원문은 `assets/models/<모델>/license.txt`에 그대로 남아 있습니다.
 */
export interface AssetCreditInfo {
  title: string;
  author: string;
  authorUrl: string;
  modelUrl: string;
  licenseName: string;
  licenseUrl: string;
  allowsCommercialUse: boolean;
}

export interface ElectricEnergySpec {
  /** 실제 주행에 사용할 수 있는 배터리 용량(kWh). gross 명목 용량이 아닙니다. */
  usableBatteryKWh: number;
  /** 주행 세션 시작 SoC(0~1). */
  initialSoc: number;
  /** 배터리가 받아들일 수 있는 최대 회생 기계출력(kW). */
  regenLimitKw: number;
  /** 배터리→바퀴, 바퀴→배터리의 대표 효율(0~1). */
  driveEfficiency: number;
  regenEfficiency: number;
  /** 짧은 주행에서 예상 주행거리를 계산할 때 쓸 복합 전비(Wh/km). */
  referenceConsumptionWhPerKm: number;
  /** 공조·전자장비 대표 소비전력(kW). */
  accessoryPowerKw: number;
}

/** 쇼룸에 진열된 차 한 대. */
export interface CarModel {
  id: string;
  /**
   * 제조사. 카드에서 차명 위에 따로 보여 줍니다.
   *
   * name에서 앞 단어를 잘라 쓰지 않는 이유: 브랜드가 한 단어라는 보장이 없습니다
   * (Land Rover, Alfa Romeo). 표기도 시대에 따라 갈립니다 — 클래식 미니는 'Mini',
   * BMW 인수 후는 'MINI'입니다. 잘라 쓰면 차를 추가할 때마다 조용히 어긋납니다.
   */
  brand: string;
  /**
   * 차명. 트림·파워트레인 등급은 넣지 않습니다.
   *
   * "Porsche Taycan Turbo S"가 아니라 "Porsche Taycan"입니다. 등급까지 적으면
   * 우리가 파는 것이 그 등급 하나라는 뜻이 되는데, 이 쇼룸은 색·휠·내장을
   * 사용자가 조합하는 곳이라 등급을 못 박을 근거가 없습니다. 에셋이 어느 등급을
   * 모델링한 것인지는 credit.title에 원본 제목으로 남아 있습니다.
   */
  name: string;
  /** 차급·파워트레인 한 줄 설명. 연식과 합쳐 부제로 보여 줍니다. */
  category: string;
  /** 구동 방식. 주행 계기판이 회전수를 띄울지 출력을 띄울지 여기서 갈립니다. */
  powertrain: Powertrain;
  shortName: string;
  /** 에셋이 속한 세대의 생산 기간. 아직 생산 중이면 end를 생략합니다. */
  productionYears: ProductionYears;
  basePrice: number;
  /**
   * 제조사가 공개한 이 사양의 최고속도 (km/h).
   *
   * 주행 물리는 SI 단위를 쓰므로 createVehicleSpec에서 m/s로 한 번만 변환합니다.
   * 이름은 트림을 숨기지만, 값은 credit.title이 가리키는 에셋 트림을 우선합니다.
   */
  topSpeedKmh: number;
  /**
   * 정지 상태에서 100km/h까지 걸리는 제조사 공개 시간 (초).
   * 주행 모델은 이 값으로 평균 전진 가속도(m/s²)를 계산합니다.
   */
  zeroTo100KmhSeconds: number;
  /**
   * 공차중량 (kg).
   *
   * 주행 물리는 가속도(m/s²)로 계산하므로 운동 자체에는 질량이 필요 없습니다.
   * 이 값은 계기판 출력(kW)을 내기 위해 있습니다 — 출력은 힘×속도이고, 가속도만으로는
   * 힘을 알 수 없습니다. topSpeedKmh와 마찬가지로 credit.title이 가리키는 트림 기준입니다.
   */
  kerbWeightKg: number;
  /**
   * 순수 전기차의 에너지 제원. ICE/PHEV는 현재 엔진·모터 출력 분배 모델이 없어 생략합니다.
   * powertrain이 ev인 카탈로그 항목은 반드시 채웁니다.
   */
  electricEnergy?: ElectricEnergySpec;
  asset: CarAsset;
  bounds: CarBounds;
  /**
   * 운전석 눈높이. 실내 지오메트리가 없는 외관 전용 에셋은 null입니다.
   * null을 허용하는 대신 hasInteriorView로 좁혀 쓰세요.
   */
  interiorEye: Vector3Tuple | null;
  /**
   * 운전석이 어느 쪽인지. interiorEye의 z 부호와 일치해야 합니다.
   * 실내가 없는 외관 전용 에셋은 향할 곳이 없으므로 생략합니다.
   */
  driverSide?: DriverSide;
  displayPanel?: DisplayPanelConfig;
  display?: DisplayConfig;
  /**
   * 번호판 설정. 에셋에 번호판 면이 있고 그 면을 따로 고를 수 있는 차량만 갖습니다.
   * selectors의 plate 역할과 함께 있어야 의미가 있습니다(둘 중 하나만 있으면
   * 그릴 곳이 없거나 무엇을 그릴지 모릅니다).
   */
  plate?: LicensePlateConfig;
  /**
   * 우리가 얹는 번호판 판. 번호판 자리는 있지만 그 면을 따로 고를 수 없는 차량용입니다.
   * plate와 자리가 겹치지 않는 한(예: plate는 뒤, platePanel은 앞) 함께 쓸 수 있습니다.
   */
  platePanel?: PlatePanelConfig;
  selectors: MeshSelectorMap;
  hidden?: MeshSelector;
  decals?: SurfaceDecalConfig[];
  credit?: AssetCreditInfo;
}

/**
 * 실내 눈높이가 확정된 차량.
 *
 * hasInteriorView가 이 타입으로 좁혀 주므로, 실내 카메라 pose를 만드는 쪽에서
 * null 검사를 다시 하지 않아도 됩니다.
 */
export type CarModelWithInterior = CarModel & { interiorEye: Vector3Tuple };

/** 위 DriverSide 주석의 규약을 코드로 옮긴 것. 실내 눈 z의 부호입니다. */
export const DRIVER_SIDE_Z: Record<DriverSide, number> = { left: -1, right: 1 };

/* ---------- 옵션 카탈로그 ---------- */

/**
 * 카탈로그 옵션의 공통 부분.
 * 목록 검증과 가격 표시가 이 부분만 쓰기 때문에 따로 뽑아 둡니다.
 */
export interface CatalogOption {
  id: string;
  name: string;
  price: number;
}

export type PaintFinish = 'metallic' | 'pearl' | 'matte';

export interface PaintOption extends CatalogOption {
  hex: string;
  finish: PaintFinish;
}

export interface WheelOption extends CatalogOption {
  finishLabel: string;
  rimColor: string;
  rimMetalness: number;
  rimRoughness: number;
}

export interface InteriorOption extends CatalogOption {
  seatColor: string;
  trimColor: string;
}

/* ---------- 배경(스튜디오) 프리셋 ---------- */

export interface StudioLight {
  position: Vector3Tuple;
  intensity: number;
}

/**
 * @react-three/drei의 <Lightformer>에 그대로 펼쳐 넣는 props입니다.
 * 이 발광 판들이 환경맵으로 구워져서 차체의 클리어코트 반사를 만듭니다.
 */
export interface LightformerConfig {
  form: 'circle' | 'ring' | 'rect';
  intensity: number;
  /** 지정하지 않으면 흰색으로 발광합니다. */
  color?: string;
  position?: Vector3Tuple;
  rotation?: Vector3Tuple;
  scale?: number | Vector3Tuple;
}

/** HDR 하단 픽셀을 평면 지면처럼 투영하는 설정. */
export interface GroundProjectionConfig {
  height: number;
  radius: number;
  scale: number;
}

/** 사진(HDRI) 기반 환경. 없으면 Lightformer로만 환경맵을 만듭니다. */
export interface PhotographicEnvironmentConfig {
  files: string;
  backgroundBlurriness?: number;
  backgroundIntensity?: number;
  environmentIntensity?: number;
  rotation?: Vector3Tuple;
  ground?: GroundProjectionConfig;
}

/**
 * 배경색·바닥·조명·환경맵을 한 세트로 묶은 프리셋.
 * 배경은 차량 사양이 아니라 뷰어 설정이므로 price가 없습니다.
 */
export interface StudioOption {
  id: string;
  name: string;
  description: string;
  backgroundColor: string;
  floorColor: string;
  floorRoughness: number;
  floorMetalness: number;
  ambientIntensity: number;
  keyLight: StudioLight;
  fillLight: StudioLight;
  contactShadowOpacity: number;
  environment?: PhotographicEnvironmentConfig;
  lightformers: LightformerConfig[];
}

export type WeatherPrecipitation = 'none' | 'rain' | 'snow';

export interface WeatherFog {
  color: string;
  near: number;
  far: number;
}

/** 배경과 독립적으로 조명·시야·노면·타이어를 바꾸는 날씨 프리셋. */
export interface WeatherOption {
  id: string;
  name: string;
  description: string;
  /** 선택 카드에 표시하는 짧은 장식 문자. */
  icon: string;
  precipitation: WeatherPrecipitation;
  precipitationIntensity: number;
  fog?: WeatherFog;
  ambientLightMultiplier: number;
  directLightMultiplier: number;
  environmentIntensityMultiplier: number;
  backgroundIntensityMultiplier: number;
  /** 0은 건조, 1은 완전히 젖은 노면. 재질과 물보라에 함께 씁니다. */
  roadWetness: number;
  /** 텍스처에 곱할 노면 색입니다. 눈이 쌓인 상태도 새 텍스처 없이 표현합니다. */
  roadTint: string;
  gripMultiplier: number;
  rollingResistanceMultiplier: number;
}

/* ---------- 선택 상태 ---------- */

/** 사용자가 고른 것. id만 담고 객체는 필요할 때 해석합니다. */
export interface Configuration {
  modelId: string;
  studioId: string;
  weatherId: string;
  paintId: string;
  wheelId: string;
  interiorId: string;
}

/** Configuration의 id를 카탈로그 객체로 해석한 결과. */
export interface SelectedOptions {
  model: CarModel;
  studio: StudioOption;
  weather: WeatherOption;
  paint: PaintOption;
  wheel: WheelOption;
  interior: InteriorOption;
}

/**
 * 가격 계산에 필요한 최소 구조.
 *
 * SelectedOptions 전체를 요구하지 않는 이유는 이 계산이 실제로 쓰는 것이
 * 기본가와 옵션 세 개의 가격뿐이기 때문입니다. 전체를 요구하면 가격 규칙만
 * 확인하려는 호출자가 관계없는 필드를 다 채워야 합니다.
 */
export interface PriceableSelection {
  model: CarModel;
  paint: Pick<CatalogOption, 'price'>;
  wheel: Pick<CatalogOption, 'price'>;
  interior: Pick<CatalogOption, 'price'>;
}
